import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import useCredits from '../hooks/useCredits';
import { trackEvent } from '../analytics';
import { consumeUpsellSource, UPSELL_FLOWS } from '../upsell/attribution';
import './PurchaseSuccessPage.css';

function PurchaseSuccessPage({ user, signIn }) {
  const { credits, loading } = useCredits(user);
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) return;
    // Dedupe on refresh/bookmark: firing purchase_completed again for the
    // same Stripe session would inflate conversion/revenue metrics.
    const sessionKey = 'tracked_purchase_' + sessionId;
    try {
      if (sessionStorage.getItem(sessionKey)) return;
      sessionStorage.setItem(sessionKey, 'true');
    } catch (e) {
      // sessionStorage unavailable (e.g. blocked) — fall through and track.
    }
    trackEvent('purchase_completed', { session_id: sessionId });
    // #350: the funnel needs a top-up event it can attribute to the touchpoint
    // that sent the user here. `purchase_completed` is deliberately left alone —
    // it is the #315 series the existing dashboards are built on — so the funnel
    // gets its own event alongside it rather than a breaking change to that one.
    // Both are behind the same session-key dedupe above.
    trackEvent('topup_purchased', {
      session_id: sessionId,
      source: consumeUpsellSource(UPSELL_FLOWS.TOPUP) || 'unknown',
    });
  }, [sessionId]);

  return (
    <div className="success-page">
      <Header user={user} signIn={signIn} />
      <div className="success-content">
        {sessionId ? (
          <>
            <div className="success-icon">&#10003;</div>
            <h1>Purchase Complete!</h1>
            <p>Your credits have been added to your account.</p>

            {!loading && credits && (
              <div className="success-balance">
                <div className="balance-label">Updated Balance</div>
                <div className="balance-value">{credits.balance} credits</div>
              </div>
            )}

            <div className="success-actions">
              <Link to="/studio">
                <button className="btn-teal">Go to Studio</button>
              </Link>
              <Link to="/credits">
                <button className="btn-teal btn-outline">
                  Buy More Credits
                </button>
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="success-icon">&#10007;</div>
            <h1>No Purchase Found</h1>
            <p>We could not verify a completed purchase. If you believe this is an error, please check your transaction history.</p>

            <div className="success-actions">
              <Link to="/credits">
                <button className="btn-teal">Back to Credits</button>
              </Link>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default PurchaseSuccessPage;
